import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { 
  Clock, 
  Star, 
  Users, 
  MapPin, 
  ArrowLeft,
  Check,
  X,
  CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Expand
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTours } from "@/hooks/useTours";
import { useTourPackages } from "@/hooks/useTourPackages";
import { PackageSelector } from "@/components/PackageSelector";

const TourDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { getTourBySlug, loading } = useTours();
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedTime, setSelectedTime] = useState("");
  const [packageSelections, setPackageSelections] = useState<{packageId: string; quantity: number}[]>([]);
  const [packageTotalPrice, setPackageTotalPrice] = useState(0);

  const tour = getTourBySlug(slug || '');
  const { packages } = useTourPackages(tour?.id);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Loading tour...</h1>
          </div>
        </div>
      </Layout>
    );
  }

  if (!tour) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Tour Not Found</h1>
            <Link to="/tours">
              <Button>Back to Tours</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const images = tour.gallery_images_urls && tour.gallery_images_urls.length > 0 
    ? tour.gallery_images_urls 
    : tour.main_image_url 
    ? [tour.main_image_url] 
    : [];

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? images.length - 1 : prev - 1
    );
  };

  const handleBookNow = () => {
    if (!selectedDate) {
      alert("Please select a pickup date");
      return;
    }
    
    if (!selectedTime) {
      alert("Please select a time");
      return;
    }

    if (tour?.pricing_type === 'package') {
      if (packageSelections.length === 0) {
        alert("Please select at least one package");
        return;
      }

      const bookingData = {
        tourName: tour.name,
        tourImage: images[0] || '',
        selectedDate: selectedDate.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        selectedTime,
        packageSelections,
        packages: packages.filter(pkg => packageSelections.some(sel => sel.packageId === pkg.id)),
        totalPrice: packageTotalPrice,
        bookingType: 'package'
      };

      navigate('/checkout', { state: bookingData });
    } else {
      const bookingData = {
        tourName: tour.name,
        tourImage: images[0] || '',
        selectedDate: selectedDate.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        adults,
        children,
        selectedTime,
        totalPrice: (tour.adult_price || 0) * adults + (tour.child_price || tour.adult_price || 0) * children,
        pricePerPerson: tour.adult_price || 0,
        bookingType: 'individual'
      };

      navigate('/checkout', { state: bookingData });
    }
  };

  return (
    <Layout>
      <div className="min-h-screen">
        {/* Breadcrumb */}
        <div className="bg-muted/30 py-4">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary">Home</Link>
              <span>/</span>
              <Link to="/tours" className="hover:text-primary">Tours</Link>
              <span>/</span>
              <span className="text-foreground">{tour.name}</span>
            </div>
            <Link to="/tours" className="inline-flex items-center gap-2 mt-4 text-primary hover:text-primary/80">
              <ArrowLeft className="h-4 w-4" />
              Back to Tours
            </Link>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Title and Rating */}
              <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary">{tour.tour_categories?.name || 'Category'}</Badge>
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-semibold">4.9</span>
                  <span className="text-muted-foreground">(150+ reviews)</span>
                </div>
              </div>
              <h1 className="text-3xl font-bold mb-2">{tour.name}</h1>
                <div className="flex items-center gap-4 text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>{tour.duration}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    <span>Max {tour.max_capacity || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    <span>{tour.location || 'Dominican Republic'}</span>
                  </div>
                </div>
              </div>

              {/* Image Gallery */}
              <div className="mb-8">
                {images.length > 0 ? (
                  <>
                    <div className="relative rounded-lg overflow-hidden">
                      <img 
                        src={images[currentImageIndex]} 
                        alt={tour.name}
                        className="w-full h-96 object-cover"
                      />
                      
                      {/* Gallery Controls */}
                      {images.length > 1 && (
                        <div className="absolute inset-0 flex items-center justify-between p-4">
                          <Button
                            variant="secondary"
                            size="icon"
                            onClick={prevImage}
                            className="bg-black/50 hover:bg-black/70 text-white"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="secondary"
                            size="icon"
                            onClick={nextImage}
                            className="bg-black/50 hover:bg-black/70 text-white"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      )}

                      {/* View Full Gallery Button */}
                      {images.length > 1 && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="secondary"
                              size="sm"
                              className="absolute bottom-4 right-4 bg-black/50 hover:bg-black/70 text-white"
                            >
                              <Expand className="h-4 w-4 mr-2" />
                              View Gallery
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-4xl">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {images.map((image, index) => (
                                <img
                                  key={index}
                                  src={image}
                                  alt={`${tour.name} ${index + 1}`}
                                  className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-80"
                                  onClick={() => setCurrentImageIndex(index)}
                                />
                              ))}
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>

                    {/* Thumbnail Navigation */}
                    {images.length > 1 && (
                      <div className="flex gap-2 mt-4">
                        {images.map((image, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            className={cn(
                              "w-20 h-16 rounded-lg overflow-hidden border-2 transition-colors",
                              currentImageIndex === index 
                                ? "border-primary" 
                                : "border-transparent hover:border-muted-foreground"
                            )}
                          >
                            <img 
                              src={image} 
                              alt={`Thumbnail ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-96 bg-muted rounded-lg flex items-center justify-center">
                    <span className="text-muted-foreground">No images available</span>
                  </div>
                )}
              </div>

              {/* General Description */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>General Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">
                    {tour.long_description || tour.short_description || 'No description available.'}
                  </p>
                </CardContent>
              </Card>

              {/* Tour Features */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Features</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {tour.features && tour.features.length > 0 ? (
                      tour.features.map((feature, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-muted-foreground text-sm">No features specified</div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Highlights */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Highlights</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-3">
                    {tour.highlights && tour.highlights.length > 0 ? (
                      tour.highlights.map((highlight, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <Star className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0 fill-yellow-500" />
                          <span className="text-sm">{highlight}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-muted-foreground text-sm">No highlights specified</div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Included Services */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>What's Included</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {tour.includes && tour.includes.length > 0 ? (
                      tour.includes.map((item, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{item}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-muted-foreground text-sm">No inclusions specified</div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Not Included Services */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>What's Not Included</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {tour.excludes && tour.excludes.length > 0 ? (
                      tour.excludes.map((item, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <X className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{item}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-muted-foreground text-sm">No exclusions specified</div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Not Suitable For */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Not Suitable For</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {tour.not_suitable_for && tour.not_suitable_for.length > 0 ? (
                      tour.not_suitable_for.map((restriction, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <X className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{restriction}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-muted-foreground text-sm">No restrictions specified</div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Tour Features Icons */}
              <Card>
                <CardHeader>
                  <CardTitle>Tour Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <Clock className="h-8 w-8 mx-auto mb-2 text-primary" />
                      <div className="font-semibold">{tour.duration}</div>
                      <div className="text-sm text-muted-foreground">Duration</div>
                    </div>
                      <div className="flex items-center space-x-1">
                        <Users className="h-8 w-8 mx-auto mb-2 text-primary" />
                        <div className="font-semibold">Max {tour.max_capacity || 'N/A'}</div>
                        <div className="text-sm text-muted-foreground">People</div>
                      </div>
                    <div className="text-center">
                      <MapPin className="h-8 w-8 mx-auto mb-2 text-primary" />
                      <div className="font-semibold">Pickup</div>
                      <div className="text-sm text-muted-foreground">Included</div>
                    </div>
                    <div className="text-center">
                      <Star className="h-8 w-8 mx-auto mb-2 text-primary fill-primary" />
                      <div className="font-semibold">4.9/5</div>
                      <div className="text-sm text-muted-foreground">Rating</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Booking Sidebar */}
            <div className="lg:col-span-1">
              <Card className="sticky top-4">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      {tour.pricing_type === 'package' ? (
                        <>
                          <CardTitle className="text-2xl">From ${tour.base_price || 'N/A'}</CardTitle>
                          <CardDescription>per package</CardDescription>
                        </>
                      ) : (
                        <>
                          <CardTitle className="text-2xl">From ${tour.adult_price || 'N/A'}</CardTitle>
                          <CardDescription>per person</CardDescription>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Pickup Date */}
                  <div>
                    <Label htmlFor="pickup-date">Pickup Date</Label>
                    <Popover> 
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal mt-1",
                            !selectedDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {selectedDate ? (
                            format(selectedDate, "PPP")
                          ) : (
                            <span>Pick a date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={setSelectedDate}
                          initialFocus
                          disabled={(date) => date < new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Conditional Rendering based on pricing type */}
                  {tour.pricing_type === 'package' ? (
                    <div>
                      <Label>Select Packages</Label>
                      <div className="mt-2">
                        <PackageSelector 
                          packages={packages}
                          onSelectionChange={(selections, totalPrice) => {
                            setPackageSelections(selections);
                            setPackageTotalPrice(totalPrice);
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    /* Individual Pricing - Travelers */
                    <div>
                      <Label>Travelers</Label>
                      <div className="space-y-3 mt-1">
                        {/* Adults */}
                        <div className="flex items-center justify-between p-3 border rounded-md">
                          <span>Adults</span>
                          <div className="flex items-center gap-3">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setAdults(Math.max(1, adults - 1))}
                            >
                              -
                            </Button>
                            <span className="w-8 text-center">{adults}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setAdults(Math.min((tour.max_capacity || 10) - children, adults + 1))}
                            >
                              +
                            </Button>
                          </div>
                        </div>
                        
                        {/* Children */}
                        <div className="flex items-center justify-between p-3 border rounded-md">
                          <span>Children</span>
                          <div className="flex items-center gap-3">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setChildren(Math.max(0, children - 1))}
                            >
                              -
                            </Button>
                            <span className="w-8 text-center">{children}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setChildren(Math.min((tour.max_capacity || 10) - adults, children + 1))}
                            >
                              +
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Available Hours */}
                  <div>
                    <Label htmlFor="available-hours">Available Hours</Label>
                    <Select value={selectedTime} onValueChange={setSelectedTime}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        {tour.schedules && tour.schedules.length > 0 ? tour.schedules.map((time, index) => (
                          <SelectItem key={index} value={time}>
                            {time}
                          </SelectItem>
                        )) : (
                          <SelectItem value="no-schedules" disabled>No schedules available</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Total */}
                  <div className="border-t pt-4">
                     <div className="flex justify-between items-center mb-4">
                       <span className="font-semibold">Total</span>
                       <span className="text-2xl font-bold">
                         ${tour.pricing_type === 'package' 
                           ? packageTotalPrice.toFixed(2)
                           : ((tour.adult_price || 0) * adults + (tour.child_price || tour.adult_price || 0) * children).toFixed(2)
                         }
                       </span>
                     </div>
                    <Button className="w-full" size="lg" onClick={handleBookNow}>
                      Book Now
                    </Button>
                  </div>

                  {/* Booking Info */}
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>• Free cancellation up to 24 hours</p>
                    <p>• Reserve now & pay later</p>
                    <p>• Instant confirmation</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TourDetail;